import { cloneDeep } from 'lodash'
import { NewPage } from 'shared-utils'

import { baseApi } from '@/app/api'
import { responseTransformer } from '@/app/api/helper/response'
import { checklistApi, ChecklistEndpoints } from '@/modules/guide/api'

import {
	CreateFormTablePayload,
	CreateSubmissionDto,
	FormImportPayload,
	FormPublishPayload,
	FormRevertToVersionPayload,
	FormTable,
	FormVersionTable,
	FormWithQuestions,
	SubmissionWithLead,
	UpdateFormTablePayload,
	UpdateWorkflowNodesPayload,
	UpsertMeetingFormPayload,
} from '../types'
import { QuestionTable } from '../types/questions'
import { questionsApi, QuestionsEndPoints } from './questions'

export enum FormsEndPoints {
	/** Query */
	getAllForms = 'getAllForms',
	getFormById = 'getFormById',
	getFormSubmissions = 'getFormSubmissions',
	getMeetingBookedForm = 'getMeetingBookedForm',
	getPublishedFormById = 'getPublishedFormById',
	getAllFormVersionsById = 'getAllFormVersionsById',
	getMinimalFormById = 'getMinimalFormById',
	getAllMinimalForms = 'getAllMinimalForms',
	getFormVersionByIdAndVersion = 'getFormVersionByIdAndVersion',
	/** Mutation */
	createForm = 'createForm',
	deleteForm = 'deleteForm',
	updateForm = 'updateForm',
	updateWorkflowNodes = 'updateWorkflowNodes',
	publishForm = 'publishForm',
	revertToFormVersion = 'revertToFormVersion',
	upsertMeetingBookedForm = 'upsertMeetingBookedForm',
	importForm = 'importForm',
	duplicateForm = 'duplicateForm',
	recaptchaVerify = 'recaptchaVerify',
	createPage = 'createPage',
	updatePage = 'updatePage',
	deletePage = 'deletePage',
	duplicatePage = 'duplicatePage',
	createTestSubmission = 'createTestSubmission',
	upsertPages = 'upsertPages',
	testLogicJumps = 'testLogicJumps',
}

const formsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		[FormsEndPoints.getFormById]: builder.query<
			FormWithQuestions,
			string | null | undefined
		>({
			query: (formId) => ({
				url: `/forms/${formId}`,
				responseHandler: responseTransformer,
			}),
			providesTags: ['GET_FORM_BY_ID'],
		}),
		[FormsEndPoints.getMeetingBookedForm]: builder.query<FormTable, void>({
			query: () => ({
				url: `/forms/meeting-booked`,
				responseHandler: responseTransformer,
			}),
			providesTags: ['GET_MEETING_BOOKED_FORM'],
		}),

		[FormsEndPoints.getAllForms]: builder.query<FormTable[], void>({
			query: () => ({
				url: `/forms`,
				responseHandler: responseTransformer,
			}),
			providesTags: ['GET_ALL_FORMS'],
		}),

		[FormsEndPoints.getFormSubmissions]: builder.query<
			SubmissionWithLead[],
			string | null | undefined
		>({
			query: (formId) => ({
				url: `/forms/${formId}/submissions`,
				responseHandler: responseTransformer,
			}),
		}),
		[FormsEndPoints.getPublishedFormById]: builder.query<
			FormVersionTable,
			string | null | undefined
		>({
			query: (formId) => ({
				url: `/forms/versions/form/${formId}`,
				responseHandler: responseTransformer,
			}),
			providesTags: ['GET_PUBLISHED_FORM_BY_ID'],
		}),
		[FormsEndPoints.getAllFormVersionsById]: builder.query<
			FormVersionTable[],
			{ formId: string; limit?: number }
		>({
			query: ({ formId, limit }) => {
				let url = `forms/versions/form/${formId}/all-versions`
				if (limit !== undefined) {
					url += `?limit=${limit}`
				}

				return {
					url,
					responseHandler: responseTransformer,
				}
			},
			providesTags: ['GET_ALL_FORM_VERSIONS_BY_ID'],
		}),
		[FormsEndPoints.getMinimalFormById]: builder.query<
			Pick<FormTable, 'id' | 'name' | 'deleted_at' | 'team_id'>,
			string | null | undefined
		>({
			query: (formId) => ({
				url: `/forms/${formId}/minimal`,
				responseHandler: responseTransformer,
			}),
		}),

		[FormsEndPoints.getAllMinimalForms]: builder.query<
			Array<
				Pick<
					FormTable,
					'id' | 'name' | 'deleted_at' | 'team_id' | 'workflow_name'
				>
			>,
			void
		>({
			query: () => ({
				url: `/forms/minimal/all`,
				responseHandler: responseTransformer,
			}),
		}),

		[FormsEndPoints.getFormVersionByIdAndVersion]: builder.query<
			FormVersionTable,
			{ formId: string; formVersion: string }
		>({
			query: ({ formId, formVersion }) => ({
				url: `forms/versions/form/${formId}/version/${formVersion}`,
				responseHandler: responseTransformer,
			}),
		}),
		/** Mutations */
		[FormsEndPoints.createForm]: builder.mutation<
			FormTable,
			CreateFormTablePayload
		>({
			query: (payload) => ({
				url: `/forms`,
				method: 'POST',
				body: payload,
				responseHandler: responseTransformer,
			}),
			invalidatesTags: ['GET_ALL_FORMS'],
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				const patchAllForms = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getAllForms,
						undefined,
						(draft) => {
							const newForm = {
								...arg.form,
							} as FormTable
							draft.unshift(newForm)
							return draft
						},
					),
				)

				const patchChecklist = dispatch(
					checklistApi.util.updateQueryData(
						ChecklistEndpoints.getChecklist,
						undefined,
						(draft) => {
							return {
								...draft,
								isFormCreated: true,
							}
						},
					),
				)

				try {
					await queryFulfilled
				} catch {
					patchAllForms.undo()
					patchChecklist.undo()
				}
			},
		}),

		[FormsEndPoints.updateForm]: builder.mutation<
			FormTable,
			UpdateFormTablePayload
		>({
			query: (payload) => ({
				url: `/forms/${payload.form.id}`,
				method: 'PATCH',
				body: {
					form: { ...payload.form, published: false },
				},
				responseHandler: responseTransformer,
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				const patchFormById = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getFormById,
						arg.form.id.toString(),
						(oldForm) => {
							return {
								...oldForm,
								...arg.form,
								published: false,
							}
						},
					),
				)

				const patchAllForms = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getAllForms,
						undefined,
						(allForms) => {
							const index = allForms.findIndex(
								(form) => form.id === arg.form.id,
							)

							if (index === -1) return allForms
							const oldForm = allForms[index]
							const newForm = {
								...oldForm,
								...arg.form,
							}
							allForms[index] = newForm
							return allForms
						},
					),
				)

				try {
					await queryFulfilled
				} catch {
					patchAllForms.undo()
					patchFormById.undo()
				}
			},
		}),

		[FormsEndPoints.deleteForm]: builder.mutation<
			FormTable,
			number | string | null | undefined
		>({
			query: (formId) => ({
				url: `/forms/${formId}`,
				method: 'DELETE',
				responseHandler: responseTransformer,
			}),

			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				const patchResult = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getAllForms,
						undefined,
						(allForms) => {
							return allForms.filter((form) => form.id !== arg)
						},
					),
				)

				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
		}),

		[FormsEndPoints.publishForm]: builder.mutation<any, FormPublishPayload>({
			query: (payload) => ({
				url: `/forms/${payload.formId}/publish`,
				method: 'POST',
			}),
			invalidatesTags: ['GET_FORM_QUESTIONS', 'GET_FORM_BY_ID'],
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				const patchFormById = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getFormById,
						arg.formId?.toString(),
						(oldForm) => {
							return {
								...oldForm,
								published: true,
							}
						},
					),
				)

				const patchChecklist = dispatch(
					checklistApi.util.updateQueryData(
						ChecklistEndpoints.getChecklist,
						undefined,
						(draft) => {
							return {
								...draft,
								isFormPublished: true,
							}
						},
					),
				)

				try {
					await queryFulfilled
				} catch {
					patchFormById.undo()
					patchChecklist.undo()
				}
			},
		}),
		[FormsEndPoints.revertToFormVersion]: builder.mutation<
			{ form: FormTable; questions: QuestionTable[] },
			FormRevertToVersionPayload
		>({
			query: (payload) => ({
				url: `/forms/${payload.formId}/revert`,
				method: 'POST',
				responseHandler: responseTransformer,
			}),
			invalidatesTags: [
				'GET_FORM_BY_ID',
				'GET_FORM_QUESTIONS',
				'GET_ALL_IMPORTED_QUESTIONS_BY_FORM_ID',
			],
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					const { data } = await queryFulfilled

					dispatch(
						formsApi.util.updateQueryData(
							FormsEndPoints.getFormById,
							arg.formId?.toString(),
							() => {
								return {
									...data.form,
									questions: data.questions,
								}
							},
						),
					)

					dispatch(
						questionsApi.util.updateQueryData(
							QuestionsEndPoints.getQuestionsByFormId,
							arg.formId?.toString(),
							() => data.questions,
						),
					)
				} catch (e) {
					console.error(e)
				}
			},
		}),
		[FormsEndPoints.upsertMeetingBookedForm]: builder.mutation<
			FormTable,
			UpsertMeetingFormPayload
		>({
			query: (payload) => ({
				url: `/forms/meeting-booked`,
				method: 'POST',
				body: payload,
				responseHandler: responseTransformer,
			}),
			invalidatesTags: ['GET_MEETING_BOOKED_FORM'],
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				const patchMeetingBookedForm = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getMeetingBookedForm,
						undefined,
						(draft) => {
							const newForm = {
								...draft,
								...arg.form,
							}
							return newForm
						},
					),
				)

				try {
					await queryFulfilled
				} catch {
					patchMeetingBookedForm.undo()
				}
			},
		}),
		[FormsEndPoints.importForm]: builder.mutation<boolean, FormImportPayload>({
			query: ({ questions, meta, formId, name }) => ({
				url: `/forms/imported/${formId}`,
				method: 'POST',
				body: { imported: { questions, meta, name } },
			}),
			invalidatesTags: [
				'GET_PUBLISHED_FORM_BY_ID',
				'GET_FORM_BY_ID',
				'GET_ALL_IMPORTED_QUESTIONS_BY_FORM_ID',
			],
		}),
		[FormsEndPoints.duplicateForm]: builder.mutation<
			FormTable,
			string | number | null | undefined
		>({
			query: (formId) => ({
				url: `/forms/${formId}/duplicate`,
				method: 'POST',
				responseHandler: responseTransformer,
			}),
			invalidatesTags: ['GET_ALL_FORMS'],
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				const patchAllForms = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getAllForms,
						undefined,
						(draft) => {
							const foundForm = draft.find((e) => e.id === arg)
							if (foundForm) {
								const newForm = cloneDeep(foundForm)
								newForm.name = `${foundForm.name} Copy`
								newForm.id = '0'
								draft.unshift(newForm)
							}

							return draft
						},
					),
				)

				try {
					await queryFulfilled
				} catch {
					patchAllForms.undo()
				}
			},
		}),

		[FormsEndPoints.createPage]: builder.mutation<
			NewPage,
			{
				formId: string
				formVersion: number
				page: Partial<NewPage>
				nextPageId: string
			}
		>({
			query: ({ formId, formVersion, page, nextPageId }) => ({
				url: `/forms/${formId}/${formVersion}/pages`,
				method: 'POST',
				body: { page, nextPageId },
				responseHandler: responseTransformer,
			}),
		}),
		[FormsEndPoints.upsertPages]: builder.mutation<
			FormTable,
			{ formId: string; pages: Array<Partial<NewPage>> }
		>({
			query: ({ formId, pages }) => ({
				url: `/forms/${formId}/pages/upsert`,
				method: 'PATCH',
				body: {
					pages,
				},
				responseHandler: responseTransformer,
			}),
			invalidatesTags: ['GET_FORM_BY_ID'],
		}),
		[FormsEndPoints.updatePage]: builder.mutation<
			FormTable,
			{ pageId: string; formId: string; page: Partial<NewPage> }
		>({
			query: ({ pageId, page }) => ({
				url: `/forms/pages/${pageId}`,
				method: 'PATCH',
				body: {
					page,
				},
				responseHandler: responseTransformer,
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				const patchFormById = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getFormById,
						arg.formId?.toString(),
						(oldForm) => {
							return {
								...oldForm,
								pagesTable: oldForm.pagesTable?.map((oldPage) => {
									if (oldPage.id === arg.pageId) {
										return {
											...oldPage,
											...arg.page,
										}
									}

									return oldPage
								}),
							}
						},
					),
				)

				try {
					await queryFulfilled
				} catch {
					patchFormById.undo()
				}
			},
		}),
		[FormsEndPoints.deletePage]: builder.mutation<
			FormTable,
			{ pageId: string; formId: string }
		>({
			query: ({ pageId, formId }) => ({
				url: `/forms/page/${pageId}`,
				method: 'DELETE',
				responseHandler: responseTransformer,
				queryFn: async (_, { dispatch }) => {
					try {
						const result = await responseTransformer(
							await fetch(`/forms/page/${pageId}`, { method: 'DELETE' }),
						)

						const formResult = await dispatch(
							formsApi.endpoints[FormsEndPoints.getFormById].initiate(formId),
						).unwrap()

						if (formResult.pagesTable) {
							const updatedPages = formResult.pagesTable
								.filter((page) => page.id !== pageId)
								.map((page, index) => ({
									...page,
									page_order: index,
								}))

							if (updatedPages.length > 0) {
								await dispatch(
									formsApi.endpoints[FormsEndPoints.upsertPages].initiate({
										formId,
										pages: updatedPages,
									}),
								).unwrap()
							}
						}

						return { data: result }
					} catch (error) {
						console.error('Failed to delete page', error)
						return {
							error: { status: 'CUSTOM_ERROR', error: 'Failed to delete page' },
						}
					}
				},
			}),
		}),
		[FormsEndPoints.duplicatePage]: builder.mutation<
			FormTable,
			{ pageId: string }
		>({
			query: ({ pageId }) => ({
				url: `/forms/pages/${pageId}/duplicate`,
				method: 'PATCH',
				responseHandler: responseTransformer,
			}),
		}),
		[FormsEndPoints.recaptchaVerify]: builder.mutation<
			{ success: boolean },
			{ token: string }
		>({
			query: ({ token }) => ({
				url: `/recaptcha/verify`,
				method: 'POST',
				body: { token },
			}),
		}),
		[FormsEndPoints.updateWorkflowNodes]: builder.mutation<
			FormTable,
			UpdateWorkflowNodesPayload
		>({
			query: (payload) => ({
				url: `/forms/${payload.workflow_id}/update-nodes`,
				method: 'PATCH',
				body: payload.workflow,
			}),
			invalidatesTags: ['GET_FORM_BY_ID', 'GET_ALL_FORMS'],
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				const patchFormById = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getFormById,
						arg.workflow_id.toString(),
						(oldForm) => {
							if (!oldForm.workflow) return oldForm

							const updatedForm = {
								...oldForm,
								workflow: {
									...oldForm.workflow,
									steps: {
										...oldForm.workflow.steps,
										...arg.workflow.steps,
									},
									startNode:
										arg.workflow.startNode || oldForm.workflow.startNode,
								},
							}

							Object.keys(updatedForm.workflow.steps).forEach((nodeId) => {
								if (updatedForm.workflow.steps[nodeId] === null) {
									delete updatedForm.workflow.steps[nodeId]
								}
							})

							return updatedForm
						},
					),
				)

				const patchAllForms = dispatch(
					formsApi.util.updateQueryData(
						FormsEndPoints.getAllForms,
						undefined,
						(allForms) => {
							const index = allForms.findIndex(
								(form) => form.id === arg.workflow_id,
							)
							if (index === -1) return allForms

							const oldForm = allForms[index]

							if (!oldForm?.workflow) return allForms

							const newForm = {
								...oldForm,
								workflow: {
									...oldForm.workflow,
									steps: {
										...oldForm.workflow.steps,
										...arg.workflow.steps,
									},
									startNode:
										arg.workflow.startNode || oldForm.workflow.startNode,
								},
							}

							Object.keys(newForm.workflow.steps).forEach((nodeId) => {
								if (newForm.workflow.steps[nodeId] === null) {
									delete newForm.workflow.steps[nodeId]
								}
							})

							allForms[index] = newForm
							return allForms
						},
					),
				)

				try {
					await queryFulfilled
				} catch {
					patchAllForms.undo()
					patchFormById.undo()
				}
			},
		}),
		[FormsEndPoints.createTestSubmission]: builder.mutation<
			SubmissionWithLead,
			{ submission: CreateSubmissionDto }
		>({
			query: (payload) => ({
				url: `forms/submissions/test/create`,
				method: 'POST',
				body: payload,
				responseHandler: responseTransformer,
			}),
		}),

		[FormsEndPoints.testLogicJumps]: builder.mutation<
			SubmissionWithLead,
			{ submission: CreateSubmissionDto }
		>({
			query: (payload) => ({
				url: `workflows/`,
				method: 'POST',
				body: payload,
				responseHandler: responseTransformer,
			}),
		}),
	}),
})

export const {
	useGetMeetingBookedFormQuery,
	useGetFormByIdQuery,
	useLazyGetFormByIdQuery,
	useCreateFormMutation,
	useGetAllFormsQuery,
	useGetPublishedFormByIdQuery,
	useLazyGetPublishedFormByIdQuery,
	useUpdateFormMutation,
	useDeleteFormMutation,
	useGetFormSubmissionsQuery,
	usePublishFormMutation,
	useRevertToFormVersionMutation,
	useUpsertMeetingBookedFormMutation,
	useImportFormMutation,
	useDuplicateFormMutation,
	useGetAllFormVersionsByIdQuery,
	useGetFormVersionByIdAndVersionQuery,
	useCreatePageMutation,
	useUpsertPagesMutation,
	useUpdatePageMutation,
	useRecaptchaVerifyMutation,
	useUpdateWorkflowNodesMutation,
	useCreateTestSubmissionMutation,
	useDeletePageMutation,
	useGetMinimalFormByIdQuery,
	useGetAllMinimalFormsQuery,
} = formsApi

export { formsApi }
